/**
 * The fleet rules, owned here ONCE for the web (owner, 26 Sep 2026; the engine's twin is
 * engine/fines_pipeline/fleet_identity.py `is_gone` / `in_fleet`):
 *
 *  - In fleet = registered with RTA (`rta_registered`) and a status that is not sold / stolen /
 *    Ready To Sell ("Ready To Sell" = already sold, only still in RTA). Every fleet count uses it:
 *    Fleet Manager cards and tabs, Smart / Summary views, the Overview tiles, Service History's
 *    Upcoming list, the KPI fleet (audit FLT-01, ADM-13, FLT-14, DUP-03).
 *  - RTA pages (Sales Invoice, Ownership Transfer, Hiaza, Free Document, Vehicle Export) keep their own
 *    view (`onRtaPages`): a sold car still in RTA stays there - they leave out only the archive marker
 *    `isSoldStatus` and a car the RTA licence reconcile marked not registered.
 *  - Registration expiry compares Dubai calendar days: a registration valid through today is not
 *    expired until tomorrow (audit FLT-22, ADM-14). One helper for the cards, the tabs and the cells.
 *  - A company car = one carrying a company / remark (the table's Status column); the Yelo Cars and
 *    Other Companies cards and tabs split on it (audit FLT-02).
 */
import { parseDateTime } from './possessionMatcher';

/** Sold / Sold To Insurance / Stolen / Ready To Sell: out of the working fleet. */
export const GONE_STATUS = /sold|stolen|sell/i;

export const isGone = (status) => GONE_STATUS.test(String(status ?? ''));

/** On the RTA master list. */
export const isRtaRegistered = (car) => car?.rta_registered === true;

/**
 * In the fleet: on the RTA list and not gone. `status` defaults to the car's own; a page that keys
 * the live status by plate may pass it.
 */
export const inFleet = (car, status = car?.status) => isRtaRegistered(car) && !isGone(status);

/**
 * The archive marker exactly: a car the old RTA master list dropped (status "sold"). Only the RTA pages
 * use it - "Sold To Insurance" / "Ready To Sell" are still registered with RTA.
 */
export const isSoldStatus = (status) => String(status ?? '').trim().toLowerCase() === 'sold';

/**
 * A car the RTA pages list: not archived as "sold", and not marked unregistered. The RTA master list used to
 * archive a car missing from it as "sold"; that upload is gone (owner, 28 Sep 2026) and the RTA licence
 * reconcile now marks such a car `rta_registered: false` instead. A "Ready To Sell" car is still registered, so it
 * stays (Ready To Sell = sold, but kept on the RTA pages); a car never reconciled (no flag) stays too.
 */
export const onRtaPages = (car) => !isSoldStatus(car?.status) && car?.rta_registered !== false;

/** True when the car carries a company / remark (not the "click to edit" placeholder). */
export const hasCompany = (car) => {
    const r = String(car?.remarks || '').trim();
    return r.length > 0 && !r.toLowerCase().includes('click to edit');
};

/** Yelo Cars vs Other Companies: the same split for the cards and the tabs they open. */
export const isCompanyCar = hasCompany;

const DAY_MS = 24 * 60 * 60 * 1000;
const dubaiDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Today's date in Dubai, "YYYY-MM-DD". */
export const dubaiToday = (now = new Date()) => dubaiDay.format(now);

/**
 * Whole days from today (Dubai) to the expiry day: 0 on the expiry day, -1 the day after, null when
 * the date cannot be read. The date's own calendar day counts (parseDateTime gives Gulf wall time).
 */
export const daysToExpiry = (value, now = new Date()) => {
    const d = parseDateTime(value);
    if (!d || Number.isNaN(d.getTime())) return null;
    const [y, m, day] = dubaiToday(now).split('-').map(Number);
    return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(y, m - 1, day)) / DAY_MS);
};

/** Expired = the expiry day is before today (Dubai). */
export const isExpired = (value, now = new Date()) => {
    const n = daysToExpiry(value, now);
    return n !== null && n < 0;
};

/** Expiring = today up to `within` days ahead (today included). */
export const isExpiringSoon = (value, now = new Date(), within = 30) => {
    const n = daysToExpiry(value, now);
    return n !== null && n >= 0 && n <= within;
};
